
import { useState, useEffect } from 'react';
import { FileUpload } from '@/components/dashboard/FileUpload';
import { ProductTable } from '@/components/dashboard/ProductTable';
import { SearchFilters } from '@/components/dashboard/SearchFilters';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { fetchCompetitorsDashboardData } from '@/store/sagas/dashboardSaga';
import { fetchCompetitors } from '@/store/sagas/competitorsSaga';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addSelectedCompetitor, removeSelectedCompetitor } from '@/store/slices/competitorsSlice';
import type { Provider } from '@/types/product';
import { Loader2 } from "lucide-react";

export default function CompetitorsDashboard() {
  const dispatch = useAppDispatch();
  const { availableCompetitors, selectedCompetitors } = useAppSelector(
    (state) => state.competitors
  );
  const isLoading = useAppSelector((state) => state.dashboard.isLoading);
  const queryLogs = useAppSelector((state) => state.dashboard.queryLogs);

  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchCompetitorsDashboardData());
    dispatch(fetchCompetitors());
  }, [dispatch]);

const handleCompetitorSelect = (providerId: string) => {
  const competitor = availableCompetitors.find(comp => comp.id === providerId);
  if (competitor) {
    dispatch(addSelectedCompetitor(competitor));
    dispatch(fetchCompetitorsDashboardData());
    setSelectedCompetitor(null); 
  }
};

  const handleCompetitorRemove = (competitor: Provider) => {
    dispatch(removeSelectedCompetitor(competitor));
    dispatch(fetchCompetitorsDashboardData());
  };

  return (
    <div className="container mx-auto p-6 space-y-6 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Product Competitors Dashboard</h1>
        <FileUpload />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <SearchFilters />
          {queryLogs.length > 0 && selectedCompetitors.length < 3 && (
            <Select value={selectedCompetitor || ""} onValueChange={handleCompetitorSelect}>
            <SelectTrigger className="w-[200px]">
              <span className="text-gray-500">
                Select competitor
              </span>
            </SelectTrigger>
            <SelectContent className="min-w-[200px]">
              {availableCompetitors.map((competitor) => (
                <SelectItem
                 key={competitor.id} 
                 value={competitor.id}
                 >
                  {competitor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>
      </div>

      <ProductTable 
        selectedCompetitors={selectedCompetitors}
        onCompetitorRemove={handleCompetitorRemove}
      />
    </div>
  );
}
